import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useNavigate } from "react-router-dom";
import Tilt from "react-parallax-tilt";

import Particle from "../Particle";
import Testimonials from "./Testimonials";
import reviews from "./data";
import useInViewOnce from "../../hooks/useInViewOnce";

import codesoilHero from "../../Assets/codesoil.jpg";

import codesoil1 from "../../Assets/code_soil1.jpg";
import codesoilplants1 from "../../Assets/code_soil_plants.jpeg";
import codesoil2 from "../../Assets/code_soil2.jpg";
import codesoil6 from "../../Assets/code_soil6.jpg";
import codesoilplants2 from "../../Assets/code_soil_plants2.jpeg";
import codesoil7 from "../../Assets/code_soil7.jpg";
import codesoilplants3 from "../../Assets/code_soil_plants3.jpeg";
import codesoil8 from "../../Assets/code_soil8.jpg";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";

const sliderImages = [
  { src: codesoil1, alt: "Code & Soil garden-tech activity" },
  { src: codesoilplants1, alt: "Code & Soil plants" },
  { src: codesoil2, alt: "Code & Soil hands-on learning" },
  { src: codesoil6, alt: "Code & Soil community garden" },
  { src: codesoilplants2, alt: "Code & Soil harvest" },
  { src: codesoil7, alt: "Code & Soil project showcase" },
  { src: codesoilplants3, alt: "Code & Soil seedlings" },
  { src: codesoil8, alt: "Code & Soil participants" },
];

const whatWeDo = [
  {
    title: "Hands-On Gardening",
    text: "From herbs to vegetables and fruit trees, participants experience the full journey from seed to harvest.",
  },
  {
    title: "Tech Integration",
    text: "Using tools like Arduino, Raspberry Pi, and beginner-friendly coding, youth build garden-tech projects such as automated irrigation, growth trackers, and garden websites.",
  },
  {
    title: "Entrepreneurship",
    text: "Learners design simple online shops, practice budgeting and marketing, and pitch their products—or give back by donating harvests to neighbors or local charities.",
  },
  {
    title: "Organic Practices",
    text: "From compost to soil, seed, and water management, participants learn eco-friendly methods that promote sustainability.",
  },
  {
    title: "Community Engagement",
    text: "Participants share knowledge and resources, fostering community and collective growth.",
  },
];

const outcomes = [
  {
    title: "Eco-Literacy",
    text: "Understanding soil, water, seeds, pests, and sustainable gardening practices.",
  },
  {
    title: "Digital Skills",
    text: "Building apps, websites, and automated systems linked to real-world gardening needs.",
  },
  {
    title: "Problem-Solving",
    text: "Using coding and organic methods to troubleshoot challenges.",
  },
  {
    title: "Collaboration",
    text: "Working in teams to grow, code, and innovate.",
  },
  {
    title: "Creativity & Innovation",
    text: "Designing natural solutions and tech prototypes that serve people and the environment.",
  },
];

function Neighborhood() {
  const navigate = useNavigate();

  const handleRegister = () => {
    navigate("/registration#codesoil-section");
  };

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2800,
    pauseOnHover: true,
    pauseOnFocus: true,
    adaptiveHeight: true,
  };

  const videoId = "5mPU-mIQgoQ";

  const { ref: topRef, hasEntered: topSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: overviewRef, hasEntered: overviewSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: whatRef, hasEntered: whatSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: outcomesRef, hasEntered: outcomesSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: beyondRef, hasEntered: beyondSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: videoRef, hasEntered: videoSeen } = useInViewOnce({ threshold: 0.18, once: true });
  const { ref: testimonialsRef, hasEntered: testimonialsSeen } = useInViewOnce({ threshold: 0.18, once: true });

  return (
    <Container fluid className="about-section codesoil-page">
      <Particle />

      <Container className="codesoil-container">
        <header className="codesoil-hero">
          <h1 className="codesoil-hero-title">
            Code <span className="purple">&amp;</span> Soil
          </h1>
          <p className="codesoil-hero-lead">Where technology meets nature and community.</p>
          <div className="codesoil-hero-cta">
            <Button onClick={handleRegister} className="custom-yellow-btn codesoil-cta-btn">
              Register Now
            </Button>
          </div>
        </header>

        <div ref={topRef} className={`codesoil-reveal${topSeen ? " is-seen" : ""}`}>
          <Row className="codesoil-top" style={{ justifyContent: "center" }}>
            <Col md={7} className="codesoil-left">
              <div className="policy-card codesoil-slider-card" aria-label="Code & Soil highlights">
                <Slider {...settings}>
                  {sliderImages.map((image, index) => (
                    <div key={index} className="codesoil-slide">
                      <img src={image.src} alt={image.alt} className="codesoil-slide-img" />
                    </div>
                  ))}
                </Slider>
              </div>
            </Col>

            <Col md={5} className="codesoil-right">
              <div className="policy-card codesoil-summary-card">
                <h2 className="codesoil-card-title">Program Snapshot</h2>
                <p className="about-text codesoil-card-text">
                  Code & Soil blends organic gardening with introductory coding to cultivate environmental responsibility and
                  practical tech development.
                </p>
                <ul className="codesoil-bullets">
                  <li>Hands-on gardening + beginner-friendly coding</li>
                  <li>Build real garden-tech prototypes</li>
                  <li>Community-centered learning and service</li>
                </ul>
                <Button onClick={handleRegister} className="custom-yellow-btn codesoil-cta-btn codesoil-cta-btn-secondary">
                  Register Here
                </Button>
              </div>

              <div className="policy-card codesoil-image-card" aria-label="Code & Soil program image">
                <Tilt glareEnable={false}>
                  <img src={codesoilHero} className="img-fluid codesoil-image" alt="Code & Soil program" />
                </Tilt>
              </div>
            </Col>
          </Row>
        </div>

        <section
          ref={overviewRef}
          className={`codesoil-section codesoil-reveal${overviewSeen ? " is-seen" : ""}`}
          aria-label="Program overview"
        >
          <Row>
            <Col md={12}>
              <div className="policy-card codesoil-copy-card">
                <h2 className="codesoil-section-heading">Overview</h2>
                <p className="codesoil-body">
                  Code &amp; Soil is a transformative initiative by Choicest Fruit that blends organic gardening with introductory
                  coding to cultivate <strong className="purple">environmental responsibility</strong> and{" "}
                  <strong className="purple">tech development</strong>. Designed for children, youth, and communities, the program
                  nurtures hands-on life skills and digital innovation—empowering participants to grow food, write code, and create
                  solutions that benefit people and the planet.
                </p>
                <p className="codesoil-body">
                  By combining the wonders of nature with the power of technology, Code &amp; Soil empowers young people to become
                  digital and environmental problem-solvers. Technology isnt just about devices—it can be a tool to nurture life,
                  foster sustainability, and strengthen communities.
                </p>
              </div>
            </Col>
          </Row>
        </section>

        <section
          ref={whatRef}
          className={`codesoil-section codesoil-reveal${whatSeen ? " is-seen" : ""}`}
          aria-label="What we do"
        >
          <h2 className="codesoil-section-heading codesoil-section-heading-center">What We Do</h2>
          <div className="codesoil-grid">
            {whatWeDo.map((item) => (
              <div key={item.title} className="policy-card codesoil-feature-card">
                <h3 className="codesoil-feature-title">{item.title}</h3>
                <p className="codesoil-feature-text">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section
          ref={outcomesRef}
          className={`codesoil-section codesoil-reveal${outcomesSeen ? " is-seen" : ""}`}
          aria-label="Core learning outcomes"
        >
          <h2 className="codesoil-section-heading codesoil-section-heading-center">Core Learning Outcomes</h2>
          <div className="codesoil-grid">
            {outcomes.map((item) => (
              <div key={item.title} className="policy-card codesoil-feature-card">
                <h3 className="codesoil-feature-title">{item.title}</h3>
                <p className="codesoil-feature-text">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section
          ref={beyondRef}
          className={`codesoil-section codesoil-reveal${beyondSeen ? " is-seen" : ""}`}
          aria-label="Beyond the garden"
        >
          <Row>
            <Col md={12}>
              <div className="policy-card codesoil-copy-card">
                <h2 className="codesoil-section-heading">Beyond the Garden</h2>
                <p className="codesoil-body">
                  Code &amp; Soil isnt just about learningits about community impact. Participants host seasonal Garden-Tech
                  Expos, showcasing their plants, apps, and inventions. Local residents are invited to mentor, volunteer, or
                  support learners by purchasing products—creating a cycle of service, learning, and empowerment.
                </p>
              </div>
            </Col>
          </Row>
        </section>

        <section
          ref={videoRef}
          className={`codesoil-section codesoil-reveal${videoSeen ? " is-seen" : ""}`}
          aria-label="Program video"
        >
          <h2 className="codesoil-section-heading codesoil-section-heading-center">Program Video</h2>
          <div className="policy-card codesoil-video-card">
            <div className="codesoil-video">
              <iframe
                title="Choicest Fruit program overview"
                src={`https://www.youtube.com/embed/${videoId}`}
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>

        <section
          ref={testimonialsRef}
          className={`codesoil-testimonials-section codesoil-reveal${testimonialsSeen ? " is-seen" : ""}`}
          aria-label="Testimonials"
        >
          <h2 className="codesoil-section-heading codesoil-section-heading-center">Testimonials</h2>
          <div className="impact-testimonials">
            <Testimonials reviews={reviews} />
          </div>
        </section>
      </Container>
    </Container>
  );
}

export default Neighborhood;
